import { useState, ReactElement } from 'react'
import {
    Box,
    Switch,
    FormControlLabel,
    Button,
    Typography,
    Divider,
    LinearProgress,
    Modal,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../redux/hook'
import { useGetSubscriptions } from '../../CustomHooks/Subscriptions/getSubscriptions'
import {
    ISensorAlertLevel,
    GlobalSubscriptions,
    IShipmentStatus,
} from '../UserGroups/interfaces'
import { useUpdateSubscriptions } from '../../CustomHooks/Subscriptions/updateSubscriptions'
import { updateLoader } from '../../redux/slices/loaderSlice'
import { useTranslation } from 'react-i18next'
import PaperModalComponent from '../Common/PaperModalComponent'
import theme from '../../theme/theme'

const defaultSubscriptions: GlobalSubscriptions = {
    sensorAlerts: {
        info: false,
        normal: false,
        warning: false,
        critical: false,
    },
    shipmentStatusAlerts: {
        activation: false,
        inTransit: false,
        waypoint: false,
        atDestination: false,
        completion: false,
    },
}
interface ISubscriptions {
    openSubscriptions: boolean
    userGroupId: string
    handleCloseSubscriptions: () => void
    readOnly: boolean
}
const Subscriptions = (props: ISubscriptions): ReactElement => {
    const { t } = useTranslation()
    const {
        openSubscriptions,
        userGroupId,
        handleCloseSubscriptions,
        readOnly,
    } = props
    const orgId = useAppSelector((state) => state.org.org?.id) || ''
    const dispatch = useAppDispatch()
    const {
        data: subscriptions = defaultSubscriptions as GlobalSubscriptions,
        // isError: isLoadingSubscriptionsError,
        // isFetching: isFetchingSubscriptions,
        isLoading: isLoadingSubscriptions,
    } = useGetSubscriptions(userGroupId, orgId)
    const { mutateAsync: updateSubscriptions } = useUpdateSubscriptions(
        userGroupId,
        orgId,
    )
    const onSaveSubscriptions = async (subscriptions: GlobalSubscriptions) => {
        dispatch(updateLoader(true))
        await updateSubscriptions(subscriptions)
        dispatch(updateLoader(false))
        handleCloseSubscriptions()
    }
    return (
        <Modal
            open={openSubscriptions}
            onClose={handleCloseSubscriptions}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <PaperModalComponent>
                <Box
                    sx={{
                        p: 4,
                        boxShadow: 4,
                        borderRadius: 4,
                        position: 'absolute' as const,
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        maxWidth: '90%',
                        backgroundColor: '#fff',
                        border: `2px solid ${theme.palette.primary.main}`,
                    }}
                >
                    {/* <Box
                        sx={{
                            backgroundColor: 'white',
                            // borderRadius: '8px',
                            boxShadow: 24,
                            padding: '20px',
                            // maxWidth: '90%',
                        }}
                    > */}
                    <Typography
                        variant="h6"
                        component="h2"
                        fontSize="18px"
                        sx={{ fontWeight: 'bold', mb: 4, cursor: 'move' }}
                        id="draggable-modal-title"
                    >
                        {t('manageSubscriptions')}
                    </Typography>
                    {isLoadingSubscriptions ? (
                        <LinearProgress />
                    ) : (
                        <SubscriptionPage
                            subscriptions={subscriptions}
                            onSaveSubscriptions={onSaveSubscriptions}
                            handleCloseSubscriptions={handleCloseSubscriptions}
                            readOnly={readOnly}
                        />
                    )}
                    {/* </Box> */}
                </Box>
            </PaperModalComponent>
        </Modal>
    )
}

export default Subscriptions

interface ISubscriptionPage {
    subscriptions: GlobalSubscriptions
    onSaveSubscriptions: (subscriptions: GlobalSubscriptions) => void
    handleCloseSubscriptions: () => void
    readOnly: boolean
}

export const SubscriptionPage = (props: ISubscriptionPage): ReactElement => {
    const { t } = useTranslation()
    const {
        subscriptions,
        onSaveSubscriptions,
        handleCloseSubscriptions,
        readOnly,
    } = props
    const [tempSubscriptions, setTempSubscriptions] = useState(subscriptions)
    const [isEdited, setIsEdited] = useState<boolean>(false)

    const handleToggleSensorAlerts = (alertLevel: ISensorAlertLevel) => {
        const category = 'sensorAlerts'
        setTempSubscriptions((prev) => ({
            ...prev,
            [category]: {
                ...prev[category],
                [alertLevel]: !prev[category][alertLevel],
            },
        }))
        setIsEdited(true)
    }

    const handleToggleShipmentStatusAlerts = (status: IShipmentStatus) => {
        const category = 'shipmentStatusAlerts'
        setTempSubscriptions((prev) => ({
            ...prev,
            [category]: {
                ...prev[category],
                [status]: !prev[category][status],
            },
        }))
        setIsEdited(true)
    }

    const handleCancel = () => {
        handleCloseSubscriptions()
    }

    const sensorAlertLevels: ISensorAlertLevel[] = [
        'critical',
        'warning',
        'info',
        'normal',
    ]

    const shipmentStatuses: IShipmentStatus[] = [
        'activation',
        'inTransit',
        'waypoint',
        'atDestination',
        'completion',
    ]

    const handleSave = () => {
        onSaveSubscriptions(tempSubscriptions)
    }

    return (
        <Box>
            <Box
                sx={{
                    mb: 5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                }}
            >
                <Typography
                    variant="h5"
                    component="h2"
                    sx={{ fontWeight: 'bold', mr: 0.5 }}
                >
                    {t('sensorAlerts')}
                </Typography>
                {sensorAlertLevels.map((alertLevel) => {
                    const value =
                        tempSubscriptions?.['sensorAlerts'][alertLevel]
                    return (
                        <FormControlLabel
                            key={alertLevel}
                            sx={{ ml: 0 }}
                            disabled={readOnly}
                            control={
                                <Switch
                                    id={`${alertLevel}-sensorAlerts`}
                                    checked={value}
                                    onChange={() =>
                                        handleToggleSensorAlerts(alertLevel)
                                    }
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        textTransform: 'capitalize',
                                    }}
                                >
                                    {t(alertLevel)}
                                </Typography>
                            }
                        />
                    )
                })}
            </Box>
            <Divider sx={{ my: 3 }} />
            <Box
                sx={{
                    mb: 5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                }}
            >
                <Typography
                    variant="h5"
                    component="h2"
                    sx={{ fontWeight: 'bold', mr: 0.5, textWrap: 'nowrap' }}
                >
                    {t('shipmentStatusAlerts')}
                </Typography>
                {shipmentStatuses.map((status) => (
                    <FormControlLabel
                        key={status}
                        sx={{ ml: 0 }}
                        disabled={readOnly}
                        control={
                            <Switch
                                id={`${status}-shipmentStatusAlerts`}
                                checked={
                                    tempSubscriptions?.shipmentStatusAlerts?.[
                                        status
                                    ]
                                }
                                onChange={() =>
                                    handleToggleShipmentStatusAlerts(status)
                                }
                            />
                        }
                        label={
                            <Typography
                                sx={{
                                    textTransform: 'capitalize',
                                    textWrap: 'nowrap',
                                }}
                            >
                                {t(status)}
                            </Typography>
                        }
                    />
                ))}
            </Box>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                {readOnly ? null : (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={!isEdited}
                        id="save-subscriptions"
                    >
                        {t('save')}
                    </Button>
                )}
                <Button
                    id="cancel-subscriptions"
                    variant="outlined"
                    onClick={handleCancel}
                >
                    {t('cancel')}
                </Button>
            </Box>
        </Box>
    )
}
